import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, ShieldCheck, ShieldAlert, FileText, ExternalLink, BadgeCheck, AlertTriangle,
} from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const addressLine = (v) => [v.address, v.city, v.state, v.zip].filter(Boolean).join(", ");
const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-sand-100 py-1.5 last:border-0">
    <span className="shrink-0 text-xs font-bold uppercase tracking-wider text-ink/40">{label}</span>
    <span className="min-w-0 text-right text-sm text-ink/80">{value || "—"}</span>
  </div>
);

/* Wizard 4 — approve a company for THIS property. Walks the two gates in the order a manager
   actually thinks: who are you · are you insured · is the paperwork here · what do you own here
   · you're on. COI is checked but never blocks approval — it blocks work. */
export default function ApproveVendorWizard({ tenantId, vendorId, vendor, onDone, onClose }) {
  const v = vendor || {};
  const link = v.link || {};
  const [coi, setCoi] = useState({
    expires_on: (v.coi || {}).expires_on || "", gl_limit: String((v.coi || {}).gl_limit || ""),
    addl_insured: true,
  });
  const [scope, setScope] = useState(link.in_scope_categories || v.trades || []);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(!!(v.paperwork || {}).ok);

  const paper = v.paperwork || {};
  const missing = (paper.missing || []).map((m) => m.label.toLowerCase());

  const verifyCoi = async () => {
    if (!coi.expires_on) return toast.error("Type the expiry printed on the certificate");
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/verify-coi`, {
        expires_on: coi.expires_on, gl_limit: coi.gl_limit || undefined, addl_insured: coi.addl_insured,
      });
      setVerified(true);
      toast.success(data.work_orders_rechecked
        ? `Verified — ${data.work_orders_rechecked} job(s) re-checked` : "Insurance verified");
    } catch (e) { toast.error(errMsg(e, "Couldn't save that")); }
    setBusy(false);
  };

  const finish = async () => {
    setBusy(true);
    try {
      if (JSON.stringify([...scope].sort()) !== JSON.stringify([...(link.in_scope_categories || [])].sort())) {
        const sa = v.site_assignment || {};
        await api.put(`/tenants/${tenantId}/field-services/vendors/${vendorId}/assignment`, {
          categories: link.categories || [], in_scope_categories: scope,
          dispatch_mode: link.dispatch_mode || "direct_then_dispatcher",
          site_assignment: {
            category_scope: sa.category_scope || [], primary_contact_id: sa.primary_contact_id || null,
            backup_contact_id: sa.backup_contact_id || null, coverage: sa.coverage || [],
          },
        });
      }
      await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/approve`);
      toast.success(`${v.name} is approved here${missing.length && !verified ? " — they can't be given a job until the paperwork lands" : ""}`);
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Could not approve them")); }
    setBusy(false);
  };

  const steps = [
    {
      key: "who", title: "Who they are",
      hint: "Straight from their application. Anything wrong here is worth a phone call before they're on your property.",
      render: () => (
        <>
          <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="av-identity">
            <Row label="Legal name" value={v.name} />
            {v.dba && <Row label="DBA" value={v.dba} />}
            <Row label="EIN / Tax ID" value={v.ein} />
            <Row label="Phone" value={v.phone} />
            <Row label="Billing email" value={v.email} />
            <Row label="Address" value={addressLine(v)} />
            <Row label="Serves" value={(v.markets || []).join(", ")} />
            <Row label="Trades" value={(v.trades || []).map((t) => CAT_LABEL[t] || t).join(", ")} />
          </div>
          {(v.licenses || []).length > 0 && (
            <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="av-licenses">
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">Licenses</div>
              {v.licenses.map((l, i) => (
                <div key={i} className="text-sm text-ink/70">
                  {[l.type, l.number && `#${l.number}`, l.state, l.expires_on && `exp ${l.expires_on}`].filter(Boolean).join(" · ")}
                </div>
              ))}
            </div>
          )}
          {v.network_status !== "approved" && !v.referred_here && (
            <WNote tone="amber" testid="av-network-note">
              Redline hasn't finished its own review of this company yet. You can still approve them here
              if you already use them — that's the fast-track, and it's on the record as your call.
            </WNote>
          )}
        </>
      ),
    },
    {
      key: "insurance", title: "Insurance",
      hint: "Read the certificate, then put what it actually says on the record. A lapsed certificate means off the property — no exceptions, no emergency override.",
      render: () => (
        <>
          {(v.coi_flags || []).length > 0 && (
            <div data-testid="av-coi-flags" className="rounded-xl border border-amber-300 bg-amber-50 p-3.5">
              <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5" /> Check these before you sign off
              </div>
              {(v.coi_flags || []).map((fl, i) => (
                <div key={i} className="text-xs font-semibold text-amber-900">• {fl.message}</div>
              ))}
            </div>
          )}
          {v.coi_file_url ? (
            <a data-testid="av-coi-view" href={v.coi_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2.5 text-sm font-bold text-ink/70 hover:border-ink/30">
              <ShieldCheck className="h-4 w-4" /> Open the certificate <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <WNote tone="amber" testid="av-no-coi">
              No certificate uploaded. If you're holding a copy in your own file you can still record its
              expiry below — that counts as you vouching for it.
            </WNote>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <WField label="Expiry on the certificate">
              <input data-testid="av-coi-expiry" type="date" value={coi.expires_on}
                onChange={(e) => setCoi({ ...coi, expires_on: e.target.value })}
                className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            </WField>
            <WField label="General liability limit">
              <input data-testid="av-coi-limit" type="number" min="0" step="100000" value={coi.gl_limit}
                placeholder="1000000" onChange={(e) => setCoi({ ...coi, gl_limit: e.target.value })}
                className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            </WField>
          </div>
          <label className="flex items-center gap-2.5 rounded-xl border border-sand-200 bg-white px-3.5 py-2.5">
            <input type="checkbox" data-testid="av-coi-addl" checked={coi.addl_insured}
              onChange={(e) => setCoi({ ...coi, addl_insured: e.target.checked })} />
            <span className="text-sm text-ink/70">We're named as additional insured</span>
          </label>
          <button data-testid="av-coi-verify" onClick={verifyCoi} disabled={busy || !coi.expires_on}
            className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40">
            <BadgeCheck className="h-4 w-4" /> I've checked it — record this
          </button>
        </>
      ),
    },
    {
      key: "paperwork", title: "Paperwork",
      hint: "This is what buys the right to bid and to be handed a job. Registering, hearing about work and walking the property need none of it.",
      render: () => (
        <>
          <div className="space-y-2" data-testid="av-paperwork">
            {[["Certificate of insurance", (v.coi || {}).expires_on || verified, (v.coi || {}).expires_on ? `expires ${v.coi.expires_on}` : ""],
              ["W-9", !!v.w9_file_url, ""],
              ["Signed agreement", !!(v.agreement || {}).signed, (v.agreement || {}).signed_at ? `signed by ${v.agreement.signed_by}` : ""]].map(([label, ok, sub]) => (
              <div key={label} className={`flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                <span>
                  <span className="block text-sm font-bold text-ink">{label}</span>
                  {sub && <span className="block text-xs text-ink/55">{sub}</span>}
                </span>
                {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  : <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" />}
              </div>
            ))}
          </div>
          {v.w9_file_url && (
            <a data-testid="av-w9-view" href={v.w9_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-xs font-bold text-ink/70 hover:border-ink/30">
              <FileText className="h-3.5 w-3.5" /> Open the W-9 <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {missing.length > 0 && !verified && (
            <WNote tone="amber" testid="av-paper-gap">
              Approving them now is fine — they'll show as “can be told about work and can price it,
              can't be handed a job” until we have {missing.join(" and ")}. We chase them for it
              automatically.
            </WNote>
          )}
        </>
      ),
    },
    {
      key: "scope", title: "What they own here",
      hint: "Anything in these trades can go straight to their crew. Anything else goes to the office and to you first — never straight to someone on site.",
      blocked: !scope.length ? "Pick at least one trade, or they'll never be routed anything automatically." : null,
      render: () => (
        <div className="flex flex-wrap gap-1.5" data-testid="av-scope">
          {Object.entries(CAT_LABEL).map(([k, label]) => {
            const on = scope.includes(k);
            const theirs = (v.trades || []).includes(k);
            return (
              <button key={k} data-testid={`av-scope-${k}`}
                onClick={() => setScope(on ? scope.filter((x) => x !== k) : [...scope, k])}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-emerald-600 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
                {label}{theirs && !on ? " ·" : ""}
              </button>
            );
          })}
        </div>
      ),
    },
    {
      key: "done", title: "Approve",
      hint: "Last look before they're live on this property.",
      render: () => (
        <>
          <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="av-summary">
            <div className="font-heading text-base font-bold text-ink">{v.name}</div>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink/60">
              <span>{scope.map((k) => CAT_LABEL[k] || k).join(", ") || "no trades picked"}</span>
              <span>·</span>
              <span>{v.routable_crew || 0} verified crew</span>
              {coi.expires_on && <><span>·</span><span>insured thru {coi.expires_on}</span></>}
            </div>
          </div>
          <WNote tone={missing.length && !verified ? "amber" : "emerald"} testid="av-final-note">
            {missing.length && !verified
              ? `They'll be active here and can price work, but nothing can be handed to them until the paperwork lands — we'll keep asking them for ${missing.join(" and ")}.`
              : "They're cleared to bid, be dispatched, and be handed a job at this property."}
          </WNote>
        </>
      ),
    },
  ];

  return (
    <Wizard testid="wiz-approve-vendor" steps={steps} busy={busy} onFinish={finish}
      finishLabel={`Approve for this ${V.lc("org")}`} finishIcon={CheckCircle2} />
  );
}
